import { useCallback, useEffect, useState } from "react";
import { Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const BASE = "/admin/tank-water-health-check/reschedule-notice-hold";

/** Hold reschedule notices until the moves settle (Sep 2026).
 *  Megan drags a visit around ServiceM8 a few times; the customer should get
 *  one message with the final time, not one per drag. */
export const RescheduleNoticeHoldPanel = () => {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(BASE);
      setState(data);
      setDraft(String(data.settings.minutes));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch, okMsg) => {
    setBusy(true);
    try {
      await api.put(BASE, patch);
      await load();
      toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/flush`);
      await load();
      toast.success(`Sent ${data.processed} held notice${data.processed === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const { settings, pending } = state;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="admin-thc-notice-hold-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[12px] font-extrabold uppercase tracking-wide text-slate-700">
            <Clock className="mr-1.5 inline h-3.5 w-3.5" />
            Office notice hold (route planning)
          </div>
          <p className="mt-1 max-w-2xl text-[12px] text-slate-600">
            While you are planning the route, moves are <b>silent for the
            customer</b> — they hear one thing only: their final arrival window,
            sent at 3:00pm the day before. This hold just batches the
            <b> office</b> SMS so a run of moves produces one notice instead of
            five. Move a booking after the customer has been told and the row
            shows <b>Notify customer of updated window</b> for you to send
            deliberately. Cancellations always go out immediately.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="admin-thc-notice-hold-toggle" disabled={busy}
            onClick={() => save({ enabled: !settings.enabled },
              settings.enabled ? "Messages now send immediately" : "Hold is on")}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-bold ${settings.enabled
              ? "border-emerald-600 bg-emerald-600 text-white"
              : "border-slate-300 bg-white text-slate-500"}`}>
            Hold: {settings.enabled ? "ON" : "OFF"}
          </button>
          <label className="text-[12px] font-bold text-slate-600">
            Wait
            <input data-testid="admin-thc-notice-hold-minutes" type="number" min="0" max="240"
              value={draft} onChange={(e) => setDraft(e.target.value)}
              className="ml-2 w-20 rounded-lg border border-slate-300 px-2 py-1 text-[12px]" />
            <span className="ml-1 font-normal text-slate-500">min</span>
          </label>
          <button data-testid="admin-thc-notice-hold-save" disabled={busy}
            onClick={() => save({ minutes: Number(draft) }, "Hold window saved")}
            className="rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-[12px] font-bold text-cyan-700 hover:bg-cyan-50">
            Save wait
          </button>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5"
        data-testid="admin-thc-notice-hold-pending">
        {pending.length === 0 ? (
          <p className="text-[12px] text-slate-500">Nothing waiting to go out.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] font-bold text-slate-700">
                {pending.length} message{pending.length === 1 ? "" : "s"} waiting
              </span>
              <button data-testid="admin-thc-notice-hold-send-now" disabled={busy}
                onClick={sendNow}
                className="rounded-full bg-slate-800 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-slate-900">
                <Send className="mr-1 inline h-3.5 w-3.5" /> Send now
              </button>
            </div>
            <ul className="mt-2 space-y-1">
              {pending.map((p) => (
                <li key={p.reference} className="text-[12px] text-slate-600"
                  data-testid={`admin-thc-notice-hold-row-${p.reference}`}>
                  <b>{p.reference}</b> — {p.changes} move{p.changes === 1 ? "" : "s"}
                  {p.last_source === "servicem8" ? " (ServiceM8)" : " (AWARE)"} ·
                  sends after {new Date(p.send_after).toLocaleTimeString([], {
                    hour: "numeric", minute: "2-digit" })}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
